import { FC, useCallback, useMemo } from 'react';
import { daysOfWeekRU } from '~/shared/const/app';
import { IDayScheduleProps, ITimeSchedule } from '~/shared/types/types';

const generateTimeSlots = (
  availableFrom: ITimeSchedule,
  availableTo: ITimeSchedule,
) => {
  const slots = [];
  let currentHour = availableFrom.hour;
  let currentQuarter = availableFrom.minute;

  while (
    currentHour < availableTo.hour ||
    (currentHour === availableTo.hour && currentQuarter <= availableTo.minute)
  ) {
    const time = `${currentHour.toString().padStart(2, '0')}:${currentQuarter
      .toString()
      .padStart(2, '0')}`;
    slots.push(time);

    currentQuarter += 15;
    if (currentQuarter >= 60) {
      currentQuarter = 0;
      currentHour += 1;
    }
  }

  return slots;
};

const shortDays = ['', '', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const daysOfWeek = daysOfWeekRU;

export const DaySchedule: FC<IDayScheduleProps> = ({
  day,
  availableFrom,
  availableTo,
  selectedInterval,
  onIntervalChange,
}) => {
  const timeSlots = useMemo(
    () => generateTimeSlots(availableFrom, availableTo),
    [availableFrom, availableTo],
  );

  const handleFromChange = useCallback(
    (time: string) => {
      const newFrom = time;
      let newTo = selectedInterval?.to || '';

      if (newTo && newFrom > newTo) {
        newTo = '';
      }

      onIntervalChange(day, { from: newFrom, to: newTo });
    },
    [day, onIntervalChange, selectedInterval?.to],
  );

  const handleToChange = useCallback(
    (time: string) => {
      const newTo = time;
      const currentFrom = selectedInterval?.from || '';

      if (currentFrom && newTo >= currentFrom) {
        onIntervalChange(day, { from: currentFrom, to: newTo });
      }
    },
    [day, onIntervalChange, selectedInterval?.from],
  );

  return (
    <div className="day-schedule">
      <h3 className="day-header">
        {daysOfWeek[day]} <span>({shortDays[day]})</span>
      </h3>

      <div className="time-columns">
        <div className="time-column">
          <h4>От</h4>
          <div className="time-slots">
            {timeSlots.map(time => (
              <div
                key={`from-${time}`}
                className={`time-slot ${selectedInterval?.from === time ? 'selected' : ''
                  }`}
                onClick={() => handleFromChange(time)}>
                {time}
              </div>
            ))}
          </div>
        </div>

        <div className="time-column">
          <h4>До</h4>
          <div className="time-slots">
            {timeSlots.map(time => {
              const isSelectable =
                selectedInterval?.from && time >= selectedInterval.from;
              const isSelected = selectedInterval?.to === time;

              return (
                <div
                  key={`to-${time}`}
                  className={`time-slot ${isSelected ? 'selected' : ''} ${!isSelectable ? 'disabled' : ''
                    }`}
                  onClick={() => isSelectable && handleToChange(time)}>
                  {time}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {selectedInterval && (
        <div className="selected-interval">
          Выбрано: {selectedInterval.from} - {selectedInterval.to}
        </div>
      )}
    </div>
  );
};
